import { Loader2, MessageCircle, MessageSquarePlus, RotateCcw, Heart, ArrowRight, Bell, MessagesSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useInteractionsBySession } from '@/hooks/useSupportInteractions';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { InteractionType } from '@/types/support.types';

const INTERACTION_CONFIG: Record<InteractionType, { label: string; icon: React.ElementType; className: string }> = {
  opened: { label: 'Abertura', icon: MessageCircle, className: 'text-blue-500 bg-blue-500/10' },
  response: { label: 'Resposta', icon: ArrowRight, className: 'text-green-500 bg-green-500/10' },
  supplement: { label: 'Complemento', icon: MessageSquarePlus, className: 'text-cyan-500 bg-cyan-500/10' },
  follow_up: { label: 'Cobrança', icon: Bell, className: 'text-amber-500 bg-amber-500/10' },
  reopened: { label: 'Reabertura', icon: RotateCcw, className: 'text-red-500 bg-red-500/10' },
  gratitude: { label: 'Agradecimento', icon: Heart, className: 'text-pink-500 bg-pink-500/10' },
};

interface InteractionsTimelineProps {
  sessionId: string | null;
}

function useMsgCount(sessionId: string | null) {
  return useQuery({
    queryKey: ['session-msg-count', sessionId],
    queryFn: async (): Promise<number> => {
      if (!sessionId) return 0;
      const { count, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('owner_session_id', sessionId);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!sessionId,
  });
}

export function InteractionsTimeline({ sessionId }: InteractionsTimelineProps) {
  const { data: interactions, isLoading } = useInteractionsBySession(sessionId);
  const { data: msgCount = 0, isLoading: isMsgCountLoading } = useMsgCount(sessionId);

  if (isLoading || isMsgCountLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!interactions || interactions.length === 0) {
    // Se há mensagens reais mas nenhuma interação formal classificada
    if (msgCount > 0) {
      return (
        <div className="flex items-start gap-2 py-3 px-3 rounded-md bg-muted/40">
          <MessagesSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground">
            Nenhuma interação classificada — ver mensagens na aba de conversa
          </p>
        </div>
      );
    }
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Nenhuma interação registrada
      </p>
    );
  }

  // Count by type
  const counts = interactions.reduce((acc, i) => {
    acc[i.type] = (acc[i.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-3">
      {/* Summary badges */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(counts).map(([type, count]) => {
          const config = INTERACTION_CONFIG[type as InteractionType];
          if (!config) return null;
          return (
            <Badge key={type} variant="secondary" className={cn('text-xs gap-1', config.className)}>
              <config.icon className="h-3 w-3" />
              {count} {config.label}
            </Badge>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="relative pl-4 space-y-0">
        <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
        {interactions.map((interaction) => {
          const config = INTERACTION_CONFIG[interaction.type];
          if (!config) return null;
          const Icon = config.icon;

          return (
            <div key={interaction.id} className="relative flex items-start gap-2 py-1.5">
              <div className={cn('relative z-10 flex items-center justify-center h-4 w-4 rounded-full border bg-card', config.className)}>
                <Icon className="h-2.5 w-2.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-medium">{config.label}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {formatDistanceToNow(new Date(interaction.created_at), { addSuffix: true, locale: ptBR })}
                  </span>
                </div>
                {interaction.notes && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words">{interaction.notes}</p>
                )}
                {interaction.auto_detected && interaction.detection_reason && (
                  <p className="text-xs text-muted-foreground/60 italic mt-0.5 line-clamp-2 break-words">{interaction.detection_reason}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
